import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PercentIcon from '@mui/icons-material/Percent'
import { AXIOS_API_INSTANCE } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment, Tooltip } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs"
import { moneyFormat, parseMoneyInput } from "../../../Utils"
import ConfirmModal from "../../modals/ConfirmModal"

export default function ServiceKitModal({
    servicekit,
    open,
    closeModal,
    creatingNewServiceKit,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [myServiceKit, setMyServiceKit] = React.useState(servicekit)
    const [saving, setSaving] = React.useState(false)
    const [deleting, setDeleting] = React.useState(false)
    const [confirmModal, setConfirmModal] = React.useState({
        open: false,
        text: '',
        callback: null
    })
    React.useEffect(() => {
        setMyServiceKit(servicekit)
    }, [servicekit])

    const handleChange = (prop) => (event) => {
        if (prop === "part_price_adjustment") {
            if ((event.nativeEvent.data === '-' && (parseInt(myServiceKit[prop]) || 0) === 0) || (event.target.value === '' && parseInt(myServiceKit[prop]) < 0)) {
                setMyServiceKit({ ...myServiceKit, [prop]: '-0' })
            } else {
                setMyServiceKit({ ...myServiceKit, [prop]: Math.max(-100, parseInt(event.target.value)) || 0 })
            }
        } else if (["default_flat_rate", "hourly_rate", "default_minutes_worked"].includes(prop)) {
            setMyServiceKit({ ...myServiceKit, [prop]: parseMoneyInput(myServiceKit[prop], event.target.value, { allow_empty: true }) || 0 })
        } else if (["has_active_times", "recurring_active_time", "taxable_flat_rate", "taxable_hourly_rate", "taxable_parts"].includes(prop)) {
            setMyServiceKit({ ...myServiceKit, [prop]: event.target.checked })
        } else if (["start_active_time", "end_active_time"].includes(prop)) {
            setMyServiceKit({ ...myServiceKit, [prop]: event })
        } else {
            setMyServiceKit({ ...myServiceKit, [prop]: event.target.value })
        }
    }

    const save = (event) => {
        setSaving(true)
        delete myServiceKit.date_created
        delete myServiceKit.date_updated
        if (creatingNewServiceKit) {
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.put(`servicekits`, myServiceKit).then(() => {
                    setSaving(false)
                    enqueueSnackbar('Successfully created service kit', { variant: 'success' })
                    closeModal(true)
                }).catch((error) => {
                    setSaving(false)
                    enqueueSnackbar(`Failed to create service kit <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        } else {
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(
                    `servicekits/${myServiceKit.id}`,
                    myServiceKit
                ).then(() => {
                    enqueueSnackbar('Successfully updated service kit', { variant: 'success' })
                    setSaving(false)
                    closeModal(true)
                }).catch((error) => {
                    setSaving(false)
                    enqueueSnackbar(`Failed to update service kit <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        }
    }
    const deleteServiceKit = () => {
        setConfirmModal({
            open: true,
            text: `Are you sure you want to delete this service kit?`,
            callback: (confirmation) => {
                if (confirmation) {
                    setDeleting(true)
                    send_authorized_request(() =>
                        AXIOS_API_INSTANCE.delete(`servicekits/${myServiceKit.id}`).then(() => {
                            enqueueSnackbar('Successfully deleted service kit', { variant: 'success' })
                            setDeleting(false)
                            closeModal(true)
                        }).catch((error) => {
                            setDeleting(false)
                            enqueueSnackbar(`Deleting service kit failed <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                        })
                    )
                }
                setConfirmModal({ ...confirmModal, open: false, text: `Are you sure you want to delete this service kit?` })
            }
        })
    }

    const expected_bill_rate = (parseFloat(myServiceKit?.default_flat_rate || 0) / (parseFloat(myServiceKit?.default_minutes_worked || 0) / 60)) + parseFloat(myServiceKit?.hourly_rate || 0)

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'md'}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                fullWidth
            >
                <LoadingBackdropIndeterminate
                    open={saving}
                    text={"Saving Service Kit"}
                />
                <LoadingBackdropIndeterminate
                    open={deleting}
                    text={"Deleting Service Kit"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>{creatingNewServiceKit ? "Creating Service Kit" : "Modifying Service Kit"}</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Grid container spacing={2}>
                            <Grid size={7}>
                                <Grid container direction="column" spacing={1}>
                                    <Grid size="grow">
                                        <TextField
                                            margin="dense"
                                            id="name"
                                            label="Name"
                                            type="text"
                                            fullWidth
                                            variant="filled"
                                            value={myServiceKit?.name ?? ''}
                                            onChange={handleChange("name")}
                                        />
                                    </Grid>
                                    <Grid size="grow">
                                        <TextField
                                            margin="dense"
                                            id="description"
                                            label="Description"
                                            type="text"
                                            fullWidth
                                            variant="filled"
                                            value={myServiceKit?.description ?? ''}
                                            onChange={handleChange("description")}
                                            multiline
                                            minRows={2}
                                        />
                                    </Grid>
                                    {false && <Grid size="grow">
                                        <Tooltip
                                            title="Only allow for this service to be selected during certain dates?"
                                            arrow
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={myServiceKit?.has_active_times || false}
                                                        onChange={handleChange("has_active_times")}
                                                    />
                                                }
                                                label="Limit Service Kit Availability"
                                            />
                                        </Tooltip>
                                    </Grid>
                                    }
                                    {myServiceKit?.has_active_times &&
                                        <Grid size="grow">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Start Active Date"
                                                    value={dayjs.utc(myServiceKit?.start_active_time)}
                                                    onChange={handleChange("start_active_time")}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                    }
                                    {myServiceKit?.has_active_times &&
                                        <Grid size="grow">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="End Active Date"
                                                    value={dayjs.utc(myServiceKit?.end_active_time)}
                                                    onChange={handleChange("end_active_time")}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                    }
                                    {myServiceKit?.has_active_times &&
                                        <Grid size="grow">
                                            <Tooltip
                                                title="Repeat this date range every year?"
                                                arrow
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={myServiceKit?.recurring_active_time || false}
                                                            onChange={handleChange("recurring_active_time")}
                                                        />
                                                    }
                                                    label="Recurring"
                                                />
                                            </Tooltip>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                            <Grid size={5}>
                                <Grid container direction="column" spacing={1}>
                                    <Grid container size="grow" alignItems={'center'}>
                                        <Grid size="grow">
                                            <TextField
                                                margin="dense"
                                                id="default_flat_rate"
                                                label="Default Flat Rate"
                                                type="number"
                                                fullWidth
                                                variant="filled"
                                                value={(myServiceKit?.default_flat_rate ?? '0').toString()}
                                                onChange={handleChange("default_flat_rate")}
                                            />
                                        </Grid>
                                        <Grid size="auto">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={myServiceKit?.taxable_flat_rate || false}
                                                        onChange={handleChange("taxable_flat_rate")}
                                                    />
                                                }
                                                label="Taxable"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container size="grow" alignItems={'center'}>
                                        <TextField
                                            margin="dense"
                                            id="flat_rate_sku"
                                            label="Flat Rate SKU (Optional)"
                                            type="text"
                                            fullWidth
                                            variant="filled"
                                            value={myServiceKit?.flat_rate_sku ?? ''}
                                            onChange={handleChange("flat_rate_sku")}
                                        />
                                    </Grid>
                                    <Grid container size="grow" alignItems={'center'}>
                                        <Grid size="grow">
                                            <TextField
                                                margin="dense"
                                                id="hourly_rate"
                                                label="Hourly Rate"
                                                type="number"
                                                fullWidth
                                                variant="filled"
                                                value={(myServiceKit?.hourly_rate ?? '0').toString()}
                                                onChange={handleChange("hourly_rate")}
                                            />
                                        </Grid>
                                        <Grid size="auto">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={myServiceKit?.taxable_hourly_rate || false}
                                                        onChange={handleChange("taxable_hourly_rate")}
                                                    />
                                                }
                                                label="Taxable"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container size="grow" alignItems={'center'}>
                                        <TextField
                                            margin="dense"
                                            id="hourly_rate_sku"
                                            label="Hourly Rate SKU (Optional)"
                                            type="text"
                                            fullWidth
                                            variant="filled"
                                            value={myServiceKit?.hourly_rate_sku ?? ''}
                                            onChange={handleChange("hourly_rate_sku")}
                                        />
                                    </Grid>
                                    <Grid size="grow">
                                        <TextField
                                            margin="dense"
                                            id="default_minutes_worked"
                                            label="Default Minutes Worked"
                                            type="number"
                                            fullWidth
                                            variant="filled"
                                            value={(myServiceKit?.default_minutes_worked ?? '0').toString()}
                                            onChange={handleChange("default_minutes_worked")}
                                        />
                                    </Grid>
                                    <Grid size="grow">
                                        <span>Expected Bill Rate: <b>${moneyFormat(expected_bill_rate)}/hr</b></span>
                                    </Grid>
                                    <Grid container size="grow" alignItems={'center'}>
                                        <Grid size="grow">
                                            <Tooltip
                                                title="Increase or decrease the price of associated parts by a set percentage"
                                                arrow
                                            >
                                                <TextField
                                                    margin="dense"
                                                    id="part_price_adjustment"
                                                    label="Part Price Adjustment"
                                                    type="number"
                                                    fullWidth
                                                    variant="filled"
                                                    value={(myServiceKit?.part_price_adjustment ?? '0').toString()}
                                                    onChange={handleChange("part_price_adjustment")}
                                                    slotProps={{
                                                        input: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    {<PercentIcon />}
                                                                    <span>{(myServiceKit?.part_price_adjustment > 0 ? '+' : '')}</span>
                                                                </InputAdornment>
                                                            )
                                                        }
                                                    }}
                                                />
                                            </Tooltip>
                                        </Grid>
                                        <Grid size="auto">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={myServiceKit?.taxable_parts || false}
                                                        onChange={handleChange("taxable_parts")}
                                                    />
                                                }
                                                label="Taxable"
                                            />
                                        </Grid>
                                    </Grid>
                                    {myServiceKit?.part_price_adjustment === -100 &&
                                        <Grid container size="grow" alignItems={'center'}>
                                            <TextField
                                                margin="dense"
                                                id="part_coupon_sku"
                                                label="Part Coupon SKU (Optional)"
                                                type="text"
                                                fullWidth
                                                variant="filled"
                                                value={myServiceKit?.part_coupon_sku ?? ''}
                                                onChange={handleChange("part_coupon_sku")}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    <ConfirmModal
                        {...confirmModal}
                    />
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                            {!creatingNewServiceKit &&
                                <Tooltip title={myServiceKit?.deletable_reason}>
                                    <span> {/* span required for tooltip events */}
                                        <Button disabled={!myServiceKit?.deletable} variant='outlined' color='error' onClick={deleteServiceKit} sx={{ mr: 1 }}>
                                            <DeleteIcon /> <Box sx={{ pl: 1 }}><span>Delete</span></Box>
                                        </Button>
                                    </span>
                                </Tooltip>
                            }
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={save}>
                                {creatingNewServiceKit ? "Create" : "Save"}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div >)
    );
}
